import React from 'react';
import PropTypes from 'prop-types';
import { get, size, drop } from 'lodash';
import { TooltipHost } from 'office-ui-fabric-react';
import DetailsList from '../DetailsList';
import Module from '../Module';
import ModuleContentList from '../ModuleContentList';
import styles from './UserList.style';

const UserList = ({ items, className, isPartner, ...other }) => {
  const sortUsersByName = users => {
    return users.sort((a, b) => a.displayName?.localeCompare(b.displayName));
  };

  const getSortedUsers = () => {
    const usersWithDisplayName = [];
    const usersWithoutDisplayName = [];
    const userList = [];
    items.forEach(user => {
      const updatedUser = user;
      if (updatedUser.role === 'partner') {
        updatedUser.vendorIds = updatedUser.vendorIds?.filter(id => id) || '';
        updatedUser.contractIds = updatedUser.contractIds?.filter(id => id) || '';
        updatedUser.contractNames = updatedUser.contractNames?.filter(id => id) || '';
      }
      if (updatedUser.displayName) {
        usersWithDisplayName.push(updatedUser);
      } else {
        // eslint-disable-next-line no-param-reassign
        updatedUser.displayName = updatedUser.key;
        usersWithoutDisplayName.push(updatedUser);
      }
    });

    userList.push(...sortUsersByName(usersWithDisplayName));
    userList.push(...sortUsersByName(usersWithoutDisplayName));

    return userList;
  };

  const columns = [
    {
      key: 'displayName',
      name: 'Name',
      fieldName: 'displayName',
      isResizable: true,
      minWidth: 350,
      maxWidth: 350,
    },
    {
      key: 'mail',
      name: 'Email',
      fieldName: 'mail',
      isResizable: true,
      minWidth: 200,
      maxWidth: 200,
    },
    // {
    //   key: 'role',
    //   name: 'Role',
    //   fieldName: 'role',
    //   isResizable: true,
    //   minWidth: 100,
    //   maxWidth: 100,
    // },
    {
      key: 'secondaryRoles',
      name: 'Secondary Roles',
      fieldName: 'secondaryRoles',
      isResizable: true,
      minWidth: 300,
      maxWidth: 300,
    },
  ];

  const partnerColumns = [
    {
      key: 'displayName',
      name: 'Name',
      fieldName: 'displayName',
      isResizable: true,
      minWidth: 200,
      maxWidth: 200,
    },
    {
      key: 'mail',
      name: 'Email',
      fieldName: 'mail',
      isResizable: true,
      minWidth: 150,
      maxWidth: 180,
    },
    {
      key: 'vendorIds',
      name: 'Vendor Ids',
      fieldName: 'vendorIds',
      isResizable: true,
      minWidth: 80,
      maxWidth: 100,
      onRender: item => {
        const otherVendorIds = drop(get(item, 'vendorIds'));
        const vendorIdsToolTip = otherVendorIds.map(vendorId => (
          <span key={vendorId}>
            {vendorId} <br />
          </span>
        ));

        const moreVendorIds = size(get(item, 'vendorIds')) - 1;
        return (
          <div className={styles.fieldText}>
            {get(item, 'vendorIds[0]')}
            {moreVendorIds > 0 && (
              <TooltipHost
                className={styles.tooltipContent}
                content={vendorIdsToolTip}
                calloutProps={{
                  gapSpace: 5,
                }}
              >
                <span className={styles.moreContents}>
                  {' '}
                  <br /> +{moreVendorIds} more
                </span>
              </TooltipHost>
            )}
          </div>
        );
      },
    },
    {
      key: 'contractIds',
      name: 'Contract Ids',
      fieldName: 'contractIds',
      isResizable: true,
      minWidth: 80,
      maxWidth: 100,
      onRender: item => {
        const otherContractIds = drop(get(item, 'contractIds'));
        const contractIdsToolTip = otherContractIds.map(contractId => (
          <span key={contractId}>
            {contractId} <br />
          </span>
        ));

        const moreContractIds = size(get(item, 'contractIds')) - 1;
        return (
          <div className={styles.fieldText}>
            {get(item, 'contractIds[0]')}
            {moreContractIds > 0 && (
              <TooltipHost
                className={styles.tooltipContent}
                content={contractIdsToolTip}
                calloutProps={{
                  gapSpace: 5,
                }}
              >
                <span className={styles.moreContents}>
                  {' '}
                  <br /> +{moreContractIds} more
                </span>
              </TooltipHost>
            )}
          </div>
        );
      },
    },
    {
      key: 'contractNames',
      name: 'Contract Names',
      fieldName: 'contractNames',
      isResizable: true,
      minWidth: 100,
      maxWidth: 150,
      onRender: item => {
        const otherContractNames = drop(get(item, 'contractNames'));
        const contractNamesToolTip = otherContractNames.map(contract => (
          <span key={contract}>
            {contract} <br />
          </span>
        ));

        const moreContractNames = size(get(item, 'contractNames')) - 1;
        return (
          <div className={styles.fieldText}>
            {get(item, 'contractNames[0]')}
            {moreContractNames > 0 && (
              <TooltipHost
                className={styles.tooltipContent}
                content={contractNamesToolTip}
                calloutProps={{
                  gapSpace: 5,
                }}
              >
                <span className={styles.moreContents}>
                  {' '}
                  <br /> +{moreContractNames} more
                </span>
              </TooltipHost>
            )}
          </div>
        );
      },
    },
    {
      key: 'secondaryRoles',
      name: 'Secondary Roles',
      fieldName: 'secondaryRoles',
      isResizable: true,
      minWidth: 100,
      maxWidth: 150,
    },
  ];

  return (
    <Module>
      <ModuleContentList>
        <DetailsList
          items={getSortedUsers().map(
            user =>
              user && {
                ...user,
                key: user.key,
                link: `users/${user.key}`,
              },
          )}
          columns={isPartner ? partnerColumns : columns}
          className={className}
          multiLine
          {...other}
        />
      </ModuleContentList>
    </Module>
  );
};
UserList.propTypes = {
  items: PropTypes.arrayOf(PropTypes.shape({})),
  className: PropTypes.string,
  isPartner: PropTypes.bool,
};

UserList.defaultProps = {
  items: [],
  className: '',
  isPartner: false,
};

export default UserList;
